import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShoppingCart, Check, Truck, Shield, Award, ChevronDown, Minus, Plus, Microscope, FileCheck2, Lock, Beaker, FileText, XCircle } from "lucide-react";
import { toast } from "sonner";
import { MolecularBg } from "../components/MolecularBg";
import { DNAHelix } from "../components/DNAHelix";
import { HexaGrid } from "../components/HexaGrid";
import { ProductCard } from "../components/ProductCard";
import { StarRating } from "../components/StarRating";
import { ReviewSection } from "../components/ReviewSection";
import { useCart, effectivePrice, isOnSale, isPreOrder as _isPreOrder, isPreOrderSoldOut, preOrderRemaining } from "../context/CartContext";
import { PriceDisplay, SaleBadge, BnplNotice } from "../components/PriceDisplay";
import { ProductImage } from "../components/ProductImage";
import { HeartButton } from "../components/HeartButton";
import { CoaRequestModal } from "../components/CoaRequestModal";
import { DispatchCountdown } from "../components/DispatchCountdown";
import { VerifiedBadge } from "../components/VerifiedBadge";
import { ProductCoaSection } from "../components/ProductCoaSection";
import { FreeShippingNudge } from "../components/FreeShippingNudge";
import { trackProductView, trackAddToCart } from "../lib/tracker";
import { useBottomSafeZone } from "../lib/floatingZones";
import { useSEO } from "../lib/useSEO";
import api from "../lib/api";

const FAQ_ITEMS = [
    {
        q: "Are these products for human consumption?",
        a: "No. All products are sold strictly for research and laboratory use only. Not for human consumption, diagnostic, or therapeutic use.",
    },
    {
        q: "How is purity verified?",
        a: "A manufacturer-supplied Certificate of Analysis is available for every product. The COA documents HPLC purity and mass spectrometry results performed by the manufacturer, represents the applicable manufacturing batch, and is provided by the manufacturer for customer reference. It is not unique to each individual vial.",
    },
    {
        q: "What is shipping like?",
        a: "Orders are dispatched within 24 hours via cold-chain courier. Domestic delivery is typically 2-3 business days. International routes available on request.",
    },
    {
        q: "Do you accept returns?",
        a: "Due to the nature of research compounds, returns are only accepted for damaged or mis-shipped items. Contact support within 48 hours of delivery.",
    },
];

export const ProductPage = () => {
    const { slug } = useParams();
    const { addItem, subtotalSale } = useCart();
    const [product, setProduct] = useState(null);
    const [related, setRelated] = useState([]);
    const [qty, setQty] = useState(1);
    const [openFaq, setOpenFaq] = useState(0);
    const [added, setAdded] = useState(false);
    const [coaOpen, setCoaOpen] = useState(false);
    const [reviewsData, setReviewsData] = useState({ reviews: [], summary: { average: 0, total: 0 } });
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setProduct(null);
        setNotFound(false);
        api.get(`/products/${slug}`)
            .then((r) => {
                setProduct(r.data);
                try { trackProductView(r.data.slug, r.data.name); } catch { /* tracking is best-effort */ }
            })
            .catch((err) => {
                if (err?.response?.status === 404) setNotFound(true);
                else setNotFound(true); // any other error → same friendly state
            });
        api.get(`/products/${slug}/related`).then((r) => setRelated(r.data)).catch(() => {});
        api.get(`/reviews/${slug}`).then((r) => setReviewsData(r.data)).catch(() => {});
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [slug]);

    useSEO(product ? {
        title: `${product.name} ${product.dosage} — Research Peptide · Carbon Labs AU`,
        description: `${product.short_description} ${product.purity} purity per the manufacturer-supplied Certificate of Analysis. AUD A$${effectivePrice(product).toFixed(2)}. For research use only.`,
        image: product.image,
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": `${product.name} ${product.dosage}`,
            "image": product.image,
            "description": product.description,
            "sku": product.lot,
            "brand": { "@type": "Brand", "name": "Carbon Labs" },
            "offers": {
                "@type": "Offer",
                "url": typeof window !== "undefined" ? window.location.href : "",
                "priceCurrency": "AUD",
                "price": effectivePrice(product).toFixed(2),
                "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            },
            ...(reviewsData.summary.total > 0 ? {
                "aggregateRating": {
                    "@type": "AggregateRating",
                    "ratingValue": reviewsData.summary.average,
                    "reviewCount": reviewsData.summary.total,
                },
                "review": reviewsData.reviews.slice(0, 10).map((r) => ({
                    "@type": "Review",
                    "reviewRating": { "@type": "Rating", "ratingValue": r.rating, "bestRating": 5 },
                    "author": { "@type": "Person", "name": r.customer_name || "Anonymous Researcher" },
                    "datePublished": r.created_at,
                    "name": r.title,
                    "reviewBody": r.body,
                })),
            } : {}),
        },
    } : { title: "Loading… · Carbon Labs", description: "" });

    const handleAdd = () => {
        if (!product) return;
        const preOrder = _isPreOrder(product);
        if (preOrder && isPreOrderSoldOut(product)) {
            toast.error("Pre-order allocation is fully reserved. Please check back soon.");
            return;
        }
        if (!preOrder && (Number(product.stock ?? 0) <= 0 || product.active === false)) {
            toast.error("This product is currently out of stock.");
            return;
        }
        const res = addItem(product, qty);
        if (res && res.ok === false) {
            toast.error("Cannot add — item is unavailable.");
            return;
        }
        try { trackAddToCart(product.slug, product.name); } catch { /* tracking is best-effort */ }
        setAdded(true);
        toast.success(preOrder ? `${product.name} pre-ordered` : `${product.name} added to cart`);
        setTimeout(() => setAdded(false), 1500);
    };

    const isPreOrder = _isPreOrder(product);
    const preOrderSoldOut = isPreOrderSoldOut(product);
    const outOfStock = product
        ? (isPreOrder ? preOrderSoldOut : (Number(product.stock ?? 0) <= 0 || product.active === false))
        : false;

    if (notFound) {
        return (
            <div className="pt-32 pb-24 container-x text-center" data-testid="product-not-found">
                <div className="max-w-md mx-auto glass-elevated rounded-2xl p-8">
                    <h1 className="font-display text-2xl font-black text-white mb-2">Product not found</h1>
                    <p className="text-sm text-[var(--text-secondary)] mb-5">
                        The peptide you&apos;re looking for may have been renamed or removed. Browse the full catalogue below.
                    </p>
                    <a href="/shop" className="btn-solid inline-flex" data-testid="product-not-found-cta">
                        Browse the shop
                    </a>
                </div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="pt-32 container-x text-center text-[var(--text-secondary)]">Loading...</div>
        );
    }

    return (
        <div className="pt-24 relative pb-32 overflow-hidden" data-testid="product-page">
            <MolecularBg intensity="low" variant="molecular" />
            {/* Subtle DNA helix drifting behind product specifications */}
            <div className="hidden lg:block absolute top-32 right-[3%] pointer-events-none z-[1]">
                <DNAHelix height={620} width={90} opacity={0.22} turns={5} />
            </div>
            {/* Partial molecular formation emerging from the bottom-left */}
            <div className="hidden md:block absolute bottom-0 left-0 pointer-events-none z-[1]">
                <HexaGrid width={420} height={420} opacity={0.28} fadeFrom="bottom-left" nodeCount={4} />
            </div>
            <div className="container-x relative z-10">
                {/* Breadcrumb */}
                <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] mb-8">
                    <Link to="/" className="hover:text-[var(--neon-cyan)]">Home</Link> /{" "}
                    <Link to={`/shop?category=${product.category}`} className="hover:text-[var(--neon-cyan)]">{product.category}</Link> /{" "}
                    <span className="text-white">{product.name}</span>
                </div>

                <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 mb-20">
                    {/* Image column */}
                    <div className="lg:col-span-6 relative">
                        <div className="cfibre-panel-cyan rounded-sm p-3 sm:p-4 relative overflow-hidden">
                            {/* Corner ticks */}
                            <span aria-hidden="true" className="absolute top-2 left-2 w-4 h-4 border-l border-t border-[var(--cyan)] z-10" />
                            <span aria-hidden="true" className="absolute top-2 right-2 w-4 h-4 border-r border-t border-[var(--cyan)] z-10" />
                            <span aria-hidden="true" className="absolute bottom-2 left-2 w-4 h-4 border-l border-b border-[var(--cyan)] z-10" />
                            <span aria-hidden="true" className="absolute bottom-2 right-2 w-4 h-4 border-r border-b border-[var(--cyan)] z-10" />
                            <ProductImage
                                product={product}
                                ratio="3 / 2"
                                className="rounded-sm"
                                loading="eager"
                                fetchPriority="high"
                            >
                                <div className="absolute top-4 left-4 flex flex-col gap-2 z-20">
                                    {product.is_new && (
                                        <span className="px-3 py-1 rounded-sm text-[10px] font-mono uppercase tracking-widest border border-[rgba(0,229,255,0.4)] bg-[rgba(0,229,255,0.08)] text-[var(--cyan)]">New</span>
                                    )}
                                    {product.is_bestseller && (
                                        <span className="px-3 py-1 rounded-sm text-[10px] font-mono uppercase tracking-widest border border-[rgba(0,229,255,0.4)] bg-[rgba(0,229,255,0.08)] text-[var(--cyan)]">Bestseller</span>
                                    )}
                                </div>
                            </ProductImage>
                            {/* Serial + lot signature */}
                            <div className="mt-3 flex items-center justify-between text-[9px] font-mono tracking-[0.28em] uppercase text-white/40 px-2">
                                <span data-testid="product-serial-tag">CL · {product.category?.slice(0, 3).toUpperCase() || "PEP"} · {String(product.lot || "N/A").slice(-6)}</span>
                                <span>Ø {product.dosage}</span>
                            </div>
                        </div>

                        {/* Mascot lockup — visible on mobile below image, sticks under on lg */}
                        <div className="hidden lg:flex mt-6 items-center gap-4 cfibre-panel rounded-sm p-4" data-testid="pdp-mascot-lockup">
                            <img
                                src="/brand/mascot.png"
                                alt="Carbon Labs mascot"
                                className="w-16 h-16 object-contain drop-shadow-[0_0_18px_rgba(0,229,255,0.35)] shrink-0"
                                loading="lazy"
                            />
                            <div className="min-w-0">
                                <div className="tech-label">// Carbon Labs · Signature</div>
                                <p className="text-xs text-[var(--text-secondary)] leading-relaxed mt-1">
                                    Assayed, cold-chained, and dispatched from our Sydney facility with the manufacturer-supplied Certificate of Analysis.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Details column */}
                    <div className="lg:col-span-6">
                        <div className="flex items-center gap-3 mb-4">
                            <span className="cyan-bar" />
                            <span className="tech-label">// {product.category}</span>
                            <span className="hidden sm:inline-block flex-1 h-px bg-[rgba(0,229,255,0.18)]" />
                            <span className="hidden sm:inline text-[9px] font-mono tracking-[0.3em] uppercase text-white/40" data-testid="product-lot-tag">
                                LOT · {product.lot}
                            </span>
                        </div>

                        <h1 className="font-display text-3xl md:text-5xl font-extrabold text-white tracking-tighter mb-4" data-testid="product-name">
                            {product.name}
                        </h1>
                        <div className="flex items-center gap-4 mb-6 flex-wrap">
                            {reviewsData.summary.total > 0 ? (
                                <StarRating value={reviewsData.summary.average} size={16} showValue count={reviewsData.summary.total} />
                            ) : (
                                <span className="px-2.5 py-1 rounded-sm text-[10px] font-mono uppercase tracking-[0.2em] border border-[rgba(0,229,255,0.4)] bg-[rgba(0,229,255,0.08)] text-[var(--cyan)]" data-testid="product-research-grade-badge">
                                    Research Grade
                                </span>
                            )}
                            <span className="text-xs font-mono text-[var(--text-muted)]">|</span>
                            {outOfStock ? (
                                <span className="text-xs font-mono uppercase tracking-widest text-red-300" data-testid="stock-count-label">
                                    {isPreOrder ? "Pre-order sold out" : "Out of stock"}
                                </span>
                            ) : isPreOrder ? (
                                <span className="text-xs font-mono uppercase tracking-widest text-[var(--gold)]" data-testid="stock-count-label">
                                    Pre-order · {Number.isFinite(preOrderRemaining(product)) ? `${preOrderRemaining(product)} left` : "unlimited"}
                                </span>
                            ) : (
                                <span className="text-xs font-mono uppercase tracking-widest text-emerald-300" data-testid="stock-count-label">In stock — {product.stock} {product.category === "lab-supplies" ? "units" : "vials"}</span>
                            )}
                        </div>

                        <p className="text-[var(--text-secondary)] leading-relaxed mb-8">{product.description}</p>

                        {/* Technical spec sheet */}
                        <div className="cfibre-panel rounded-sm overflow-hidden mb-8" data-testid="product-spec-sheet">
                            <div className="px-4 py-2.5 border-b border-[rgba(0,229,255,0.18)] flex items-center justify-between bg-[rgba(0,229,255,0.03)]">
                                <span className="tech-label">// Specification</span>
                                <span className="text-[9px] font-mono tracking-[0.28em] uppercase text-white/40">V.2.6</span>
                            </div>
                            <dl className="divide-y divide-white/5">
                                {[
                                    { k: "Dosage", v: product.dosage },
                                    { k: "Purity", v: product.purity, cyan: true },
                                    { k: "Lot No.", v: product.lot, mono: true },
                                    { k: "Storage", v: "2–8°C · Refrigerated", mono: true },
                                    { k: "Category", v: product.category, mono: true },
                                    { k: "Use", v: "Research only · not for human consumption" },
                                ].map((row) => (
                                    <div key={row.k} className="flex items-center px-4 py-2.5 gap-3">
                                        <dt className="text-[10px] font-mono uppercase tracking-[0.28em] text-white/50 w-24 shrink-0">{row.k}</dt>
                                        <dd
                                            className={`flex-1 text-right sm:text-left ${row.mono ? "font-mono text-sm" : "font-display text-sm font-semibold"} ${row.cyan ? "text-[var(--cyan-hi)]" : "text-white"}`}
                                        >
                                            {row.v}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        </div>

                        <div className="mb-8">
                            <div className="tech-label mb-3">// Research Applications</div>
                            <ul className="space-y-2">
                                {product.benefits.map((b, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm text-[var(--text-secondary)]">
                                        <Check size={14} className="text-[var(--cyan)] mt-0.5 flex-shrink-0" />
                                        {b}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="flex items-center gap-3 mb-2">
                            <div className={`flex items-center border border-[rgba(0,229,255,0.25)] bg-black/30 rounded-sm ${outOfStock ? "opacity-40 pointer-events-none" : ""}`}>
                                <button onClick={() => setQty((q) => Math.max(1, q - 1))} disabled={outOfStock} className="p-3 text-white hover:text-[var(--cyan)] disabled:cursor-not-allowed" data-testid="qty-decrement" aria-label="Decrease">
                                    <Minus size={14} />
                                </button>
                                <span className="px-3 font-mono text-white min-w-[2rem] text-center" data-testid="qty-display">{qty}</span>
                                <button onClick={() => setQty((q) => Math.min(Math.min(20, Number(product.stock || 20)), q + 1))} disabled={outOfStock} className="p-3 text-white hover:text-[var(--cyan)] disabled:cursor-not-allowed" data-testid="qty-increment" aria-label="Increase">
                                    <Plus size={14} />
                                </button>
                            </div>
                            <div className="font-display text-3xl font-extrabold text-white" data-testid="product-price">
                                A${(effectivePrice(product) * qty).toFixed(2)}
                            </div>
                            {isOnSale(product) && (
                                <span
                                    className="font-mono text-base text-[var(--text-muted)] line-through"
                                    data-testid="product-rrp"
                                >
                                    A${(product.price * qty).toFixed(2)}
                                </span>
                            )}
                        </div>
                        {isOnSale(product) && (
                            <div className="flex items-center gap-2 mb-6" data-testid="product-sale-meta">
                                <SaleBadge product={product} />
                                <span className="text-xs font-mono uppercase tracking-widest text-emerald-300">
                                    Save A${((product.price - effectivePrice(product)) * qty).toFixed(2)}
                                </span>
                            </div>
                        )}
                        {!isOnSale(product) && <div className="mb-6" />}

                        <BnplNotice product={product} quantity={qty} size="md" className="mb-5 -mt-3" />

                        <FreeShippingNudge
                            projectedSubtotal={Number(subtotalSale || 0) + effectivePrice(product) * qty}
                            className="mb-4"
                        />

                        <div className="flex flex-wrap gap-3">
                            <button
                                onClick={handleAdd}
                                disabled={outOfStock}
                                className={`btn-solid flex-1 min-w-[200px] ${outOfStock ? "opacity-50 cursor-not-allowed pointer-events-none" : "pulse-glow"} ${isPreOrder ? "!bg-[var(--gold)] !text-black" : ""}`}
                                data-testid={isPreOrder ? "pre-order-main" : "add-to-cart-main"}
                            >
                                {outOfStock ? (
                                    <><XCircle size={16} /> Out of Stock</>
                                ) : added ? (
                                    <><Check size={16} /> {isPreOrder ? "Pre-ordered" : "Added"}</>
                                ) : isPreOrder ? (
                                    <><ShoppingCart size={16} /> Pre-Order Now</>
                                ) : (
                                    <><ShoppingCart size={16} /> Add to Cart</>
                                )}
                            </button>
                            <HeartButton product={product} size="lg" variant="ghost" />
                            <Link to="/cart" className="btn-neon" data-testid="view-cart-btn">
                                View Cart
                            </Link>
                        </div>
                        {isPreOrder && (
                            <div
                                className="mt-3 p-3 rounded-xl border border-[var(--gold)]/40 bg-[var(--gold)]/5 text-xs font-mono text-[var(--gold)]"
                                data-testid="pre-order-notice"
                            >
                                <div className="uppercase tracking-widest font-bold mb-1">Pre-order</div>
                                <div className="text-[11px] normal-case tracking-normal text-[var(--text-secondary)] space-y-0.5">
                                    {product.pre_order?.estimated_dispatch_date && (
                                        <div>Expected dispatch: <span className="text-white">{product.pre_order.estimated_dispatch_date}</span></div>
                                    )}
                                    {product.pre_order?.expected_arrival_date && (
                                        <div>Stock arrives with us: <span className="text-white">{product.pre_order.expected_arrival_date}</span></div>
                                    )}
                                    {product.pre_order?.message && (
                                        <div className="text-white/90">{product.pre_order.message}</div>
                                    )}
                                    {preOrderSoldOut ? (
                                        <div className="text-red-300 uppercase tracking-widest font-bold">Pre-order sold out</div>
                                    ) : (Number.isFinite(preOrderRemaining(product)) && (
                                        <div>Pre-order allocation left: <span className="text-white">{preOrderRemaining(product)}</span></div>
                                    ))}
                                    <div>You&apos;ll be charged now and notified when the item ships.</div>
                                </div>
                            </div>
                        )}
                        {outOfStock && !isPreOrder && (
                            <div className="mt-3 p-3 rounded-xl border border-red-500/30 bg-red-500/5 text-xs font-mono uppercase tracking-widest text-red-300" data-testid="oos-notice">
                                This product is currently out of stock. Check back soon.
                            </div>
                        )}

                        {/* Dispatch countdown */}
                        <div className="mt-5">
                            <DispatchCountdown />
                        </div>

                        {/* Request COA */}
                        <button
                            onClick={() => setCoaOpen(true)}
                            className="mt-4 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[var(--gold)] hover:text-[#F5C932] hover:underline"
                            data-testid="request-coa-btn"
                        >
                            <FileText size={13} /> Request manufacturer-supplied COA
                        </button>

                        <CoaRequestModal product={product} open={coaOpen} onClose={() => setCoaOpen(false)} />

                        {/* Verified Documentation badge */}
                        {product.has_coa && (
                            <div className="mt-3" data-testid="pdp-verified-badge-wrap">
                                <VerifiedBadge product={product} />
                            </div>
                        )}

                        {/* Trust indicators */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-8" data-testid="product-trust-row">
                            {(product.category === "lab-supplies" ? [
                                { i: Beaker, t: "Sterile Lab Grade" },
                                { i: Lock, t: "Secure Checkout" },
                                { i: Truck, t: "Australian Dispatch" },
                                { i: Award, t: "USP-Grade" },
                            ] : [
                                { i: Microscope, t: "Manufacturer COA" },
                                { i: FileCheck2, t: "Manufacturer-Supplied COA" },
                                { i: Lock, t: "Secure Checkout" },
                                { i: Truck, t: "Australian Dispatch" },
                            ]).map((it, i) => (
                                <div key={i} className="flex items-center gap-2 p-3 cfibre-panel rounded-sm">
                                    <it.i size={14} className="text-[var(--cyan)] flex-shrink-0" />
                                    <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-secondary)] leading-tight">{it.t}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Research overview + Storage */}
                {(product.research_overview || product.storage) && (
                    <div className="grid md:grid-cols-2 gap-4 mb-16" data-testid="product-info-section">
                        {product.research_overview && (
                            <div className="cfibre-panel rounded-sm p-6 relative overflow-hidden">
                                <span aria-hidden="true" className="absolute top-2 right-2 w-4 h-4 border-r border-t border-[var(--cyan)]" />
                                <div className="tech-label mb-3">// Research Overview</div>
                                <h3 className="font-display uppercase font-bold text-white text-lg mb-3">Background</h3>
                                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{product.research_overview}</p>
                            </div>
                        )}
                        {product.storage && (
                            <div className="cfibre-panel rounded-sm p-6 relative overflow-hidden">
                                <span aria-hidden="true" className="absolute top-2 right-2 w-4 h-4 border-r border-t border-[var(--cyan)]" />
                                <div className="tech-label mb-3">// Handling</div>
                                <h3 className="font-display uppercase font-bold text-white text-lg mb-3">Storage & Reconstitution</h3>
                                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{product.storage}</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Reviews */}
                <ProductCoaSection slug={product.slug} />

                <ReviewSection productSlug={product.slug} />

                {/* FAQ */}
                <section className="mt-20" data-testid="product-faq-section">
                    <div className="flex items-center gap-3 mb-8">
                        <span className="cyan-bar" />
                        <span className="tech-label">// Questions</span>
                    </div>
                    <h2 className="font-display text-2xl md:text-4xl font-extrabold text-white tracking-tight mb-8">FAQ</h2>
                    <div className="space-y-3">
                        {FAQ_ITEMS.map((f, i) => (
                            <div key={i} className="cfibre-panel rounded-sm overflow-hidden" data-testid={`faq-item-${i}`}>
                                <button
                                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                                    className="w-full p-5 flex items-center justify-between text-left"
                                >
                                    <span className="font-display font-semibold text-white">{f.q}</span>
                                    <ChevronDown size={18} className={`text-[var(--cyan)] transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                                </button>
                                {openFaq === i && (
                                    <div className="px-5 pb-5 text-sm text-[var(--text-secondary)] leading-relaxed border-t border-[rgba(0,229,255,0.12)] pt-4">{f.a}</div>
                                )}
                            </div>
                        ))}
                    </div>
                </section>

                {/* Related */}
                {related.length > 0 && (
                    <section className="mt-20" data-testid="related-products-section">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="cyan-bar" />
                            <span className="tech-label">// You may also like</span>
                        </div>
                        <h2 className="font-display text-2xl md:text-4xl font-extrabold text-white tracking-tight mb-8">Related Products</h2>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 lg:gap-6">
                            {related.map((p) => <ProductCard key={p.slug} product={p} />)}
                        </div>
                    </section>
                )}
            </div>

            {/* Sticky add-to-cart bar (mobile) */}
            <StickyAddBar product={product} qty={qty} handleAdd={handleAdd} outOfStock={outOfStock} />
        </div>
    );
};

const StickyAddBar = ({ product, qty, handleAdd, outOfStock }) => {
    const ref = React.useRef(null);
    useBottomSafeZone(ref, true);
    return (
        <div
            ref={ref}
            className="lg:hidden fixed bottom-0 left-0 right-0 z-40 sticky-cart-bar"
            data-testid="sticky-add-bar"
        >
            {/* Trust strip — surfaces purchase confidence at the moment of decision */}
            <div
                className="flex items-center justify-center gap-3 px-4 py-1.5 border-b border-white/5 text-[9px] font-mono uppercase tracking-[0.2em] text-[var(--text-muted)] bg-black/40"
                data-testid="sticky-trust-strip"
            >
                <span className="flex items-center gap-1">
                    <Truck size={10} className="text-[var(--tiffany)]" />
                    Sydney dispatch
                </span>
                <span className="opacity-30">·</span>
                <span className="flex items-center gap-1">
                    <Lock size={10} className="text-[var(--tiffany)]" />
                    Secure
                </span>
                <span className="opacity-30">·</span>
                <span className="flex items-center gap-1">
                    <span aria-hidden="true">🇦🇺</span>
                    AU
                </span>
            </div>

            <div className="p-4 flex items-center gap-3">
                <div className="flex-1">
                    <div className="text-xs text-[var(--text-muted)] font-mono uppercase tracking-widest">{product.name}</div>
                    <div className="font-display text-xl font-bold text-white">A${(effectivePrice(product) * qty).toFixed(2)}</div>
                </div>
                <button
                    onClick={handleAdd}
                    disabled={outOfStock}
                    className={`btn-solid ${outOfStock ? "opacity-50 cursor-not-allowed" : ""}`}
                    data-testid="sticky-add-btn"
                >
                    {outOfStock ? <><XCircle size={14} /> Out of Stock</> : <><ShoppingCart size={14} /> Add</>}
                </button>
            </div>
        </div>
    );
};
